/**
 * The "light" LandXML load: a layer's ALIGNMENTS, and nothing else.
 *
 * Alignments reach `alignmentsAtom` from `LandXML.tsx`, which the viewer
 * mounts only for the layers the user has switched ON (`selectedLayersAtom`).
 * A project whose alignments live in a LandXML the user has not switched on
 * therefore has no alignments at all as far as the station-volumes section is
 * concerned — the enable checkbox is inert, and the only way out is to leave
 * the analysis, switch the layer on, come back and rebuild the configuration.
 *
 * This module is the way back in: given the file's text it produces exactly
 * the `Alignment`s the full load would register, with the same keys, and
 * builds no geometry, no TIN, no LOD and no sampler. It is deliberately NOT
 * automatic — a LandXML can be hundreds of megabytes, and nobody should pay
 * for that on the off-chance the file has a centreline in it.
 *
 * Two deliberate differences from the full load, both harmless:
 *
 *  - No surface sampler, so an alignment whose `<CoordGeom>` is flat and which
 *    has no `<Profile>` keeps `zSource: 'NONE'` where the full load could have
 *    draped it onto the file's own TIN. The station-volumes section already
 *    handles that case — it is the "no vertical profile" note and the
 *    "Drape onto" picker — and volumes never depended on alignment Z anyway.
 *  - No layer transform. The caller applies it, exactly as `LandXML.tsx` does
 *    at registration time, so a settings change needs no re-parse.
 */
import { alignmentKey, buildAlignment, type Alignment } from '@/core/alignment';
import type { TextLike } from '@/core/chunkedText';
import { parseAlignments } from '@/core/landxml';
import { extractTins } from '@/core/landxmlTin';

/**
 * Parse one LandXML document's alignments.
 *
 * Returns `[]` — never throws — for anything that is not a LandXML document
 * with alignments in it: an S3/CloudFront XML error page, an HTML 404 body,
 * a surfaces-only DTM, or a file in a unit the parser refuses. The caller is
 * a button in a dialog, and "this file has no alignments" is the honest
 * answer to all of those.
 */
export function alignmentsFromLandxmlText(text: TextLike, layerId: string): Alignment[] {
  let doc: Document;
  try {
    // TINs first, exactly as the full load does: they are ~99 % of a DTM's
    // bytes and handing them to DOMParser is what makes these files unusable.
    // The tins themselves are dropped on the floor here.
    const { lightText } = extractTins(text);
    doc = new DOMParser().parseFromString(lightText, 'application/xml');
    if (doc.querySelector('parsererror')) return [];
  } catch {
    return [];
  }

  let features;
  try {
    features = parseAlignments(doc);
  } catch (err) {
    // `LandxmlNonMetreUnitsError` and anything else the parser refuses on.
    console.warn(`[landxml] alignments could not be read for layer ${layerId}:`, err);
    return [];
  }

  // The same `${category}:${name}:${ordinal}` scheme `LandXML.tsx` uses. The
  // ordinal counts within one category+name, so counting over the alignments
  // alone — rather than over every feature in the file, which is what the
  // full load walks — produces identical ids. That equality is the point: the
  // full load re-registers under the same owner key when the layer is later
  // switched on, and a key that moved would silently deselect the alignment
  // the user had chosen.
  const ordinals = new Map<string, number>();
  const out: Alignment[] = [];
  for (const f of features) {
    if (f.category !== 'ALIGNMENT' || !f.metadata.alignment) continue;
    const base = `${f.category}:${f.name}`;
    const ord = ordinals.get(base) ?? 0;
    ordinals.set(base, ord + 1);
    const a = buildAlignment({
      key: alignmentKey(layerId, `${base}:${ord}`),
      source: 'LANDXML',
      layerId,
      featureId: `${base}:${ord}`,
      name: f.name,
      points: f.metadata.alignment.points,
      staStart: f.metadata.alignment.staStart,
      equations: f.metadata.alignment.equations,
      keyPoints: f.metadata.alignment.keyPoints,
      degradations: f.metadata.alignment.degradations,
      zSource: f.metadata.alignment.zSource,
    });
    if (a) out.push(a);
  }
  return out;
}
